import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import apiClient, { get, patch } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { Pencil } from 'lucide-react';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { puedeEditarCargoContenedor } from '../../config/resources.finanzas-cxc.jsx';

/**
 * Cargos a Contenedor — página BESPOKE (no `ResourcePage` genérico + `FormModal`).
 *
 * TAREA 2 de la auditoría 2026-09-28 (ver reporte): el `ResourcePage` genérico
 * (usado antes aquí vía `RESOURCES_FINANZAS_CXC['cargos-contenedor']`, ver
 * `src/config/resources.finanzas-cxc.jsx`) no soporta una calculadora ligada
 * USD/tipoCambio/total en el formulario — solo inputs `type:'number'` sueltos.
 * V1 (`IbuFront/src/components/views/Admin/CargosContenedor/
 * ListadoCargosContenedor.jsx`, funciones `calcula`/`calculaTipoCambio` líneas
 * ~503-510 y el bloque de Inputs condicionados por `moneda` líneas ~875-940) SÍ
 * tiene esa calculadora, confirmada así:
 *   - moneda USD: `total = totalUSD * tipoCambio` (editar cualquiera de los 2
 *     recalcula `total`; `total` es de solo lectura en ese caso).
 *   - moneda MXN: solo se captura `total`; `totalUSD = total / tipoCambio`
 *     (`tipoCambio` se mantiene, normalmente 1).
 * Esta fórmula es EXACTAMENTE la misma que ya implementa
 * `src/pages/finanzas-cxp/GastosContenedorPage.jsx` (Gastos de Contenedor, CXP) —
 * este archivo replica ese patrón bespoke (fetch + tabla + paginación cliente,
 * mismo motivo que documenta `finanzas-cxp/shared.js`: `GET /cargos-contenedor`
 * tampoco pagina en servidor, ver `cargos-contenedor.controller.js#findAll` ->
 * `res.success(await cargosContenedorService.listar())`, sin `.paginate()`).
 *
 * Reglas de negocio del backend (`cargos-contenedor.service.js`, confirmadas
 * contra el validator y el auditado `resources.finanzas-cxc.jsx`):
 *   - Alta: `POST /cargos-contenedor` con `abonos:0, saldo:total` (mismo criterio
 *     que el `ResourcePage` que reemplaza).
 *   - Edición: DEBE ir a `PATCH /cargos-contenedor/:id/importe`
 *     (`editarImporte()`), NUNCA al PATCH genérico (`editar()`, que no reajusta
 *     `Clientes.cargos/saldo` ni `Contenedores.totalCargos` — bug real ya
 *     documentado en `resources.finanzas-cxc.jsx` líneas 118-129). Este bespoke
 *     mantiene ese mismo enrutado.
 *   - "Pagar": `PATCH /cargos-contenedor/:id/pagar {saldo: monto}` (nombre de
 *     campo heredado de V1 — es el MONTO ABONADO, no el saldo resultante).
 *   - Baja: `DELETE /cargos-contenedor/:id` (baja lógica -> `cancelar()`).
 */
const RECURSO = 'cargos';
const ROUTE = 'cargos-contenedor';

function valoresIniciales(row) {
  if (!row) {
    return {
      fecha: today(), contenedores: '', clientes: '', conceptosGastos: '', descripcion: 'NA',
      moneda: 'MXN', totalUSD: 0, tipoCambio: 1, total: 0,
    };
  }
  return {
    fecha: row.fecha, contenedores: row._idContenedor, clientes: row._idCliente,
    conceptosGastos: row._idConcepto, descripcion: row.descripcion, moneda: row.moneda || 'MXN',
    totalUSD: row.totalUSD, tipoCambio: row.tipoCambio, total: row.total,
  };
}

function CargoContenedorForm({ open, onClose, title, initial, conceptos, contenedores, onSubmit, esEdicion }) {
  const [v, setV] = useState(valoresIniciales(initial));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales(initial)); }, [open, initial]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  function seleccionaContenedor(id) {
    const c = contenedores.find((x) => x._id === id);
    const cliente = primero(c?.clientes);
    setV((s) => ({ ...s, contenedores: id, clientes: cliente?._id || '' }));
  }

  function cambiaMoneda(m) { setV((s) => ({ ...s, moneda: m, totalUSD: 0, tipoCambio: 1, total: 0 })); }
  // ---- Calculadora ligada USD <-> tipoCambio <-> total (mismo patrón que GastosContenedorPage.jsx) ----
  function cambiaTotalUSD(val) { const totalUSD = Number(val) || 0; setV((s) => ({ ...s, totalUSD, total: totalUSD * (Number(s.tipoCambio) || 0) })); }
  function cambiaTipoCambio(val) { const tipoCambio = Number(val) || 0; setV((s) => ({ ...s, tipoCambio, total: (Number(s.totalUSD) || 0) * tipoCambio })); }
  function cambiaTotalMXN(val) { const total = Number(val) || 0; setV((s) => ({ ...s, total, totalUSD: s.tipoCambio ? total / s.tipoCambio : 0 })); }

  const clienteNombre = (() => {
    const c = contenedores.find((x) => x._id === v.contenedores);
    return primero(c?.clientes)?.nombre_comercial || '—';
  })();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!v.conceptosGastos || !v.fecha || !v.contenedores || !v.clientes) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Favor de completar todos los campos' });
      return;
    }
    setGuardando(true);
    try {
      await onSubmit(v);
      Swal.fire('Good job!', esEdicion ? 'Actualizado con exito' : 'Creado con exito', 'success');
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="cargo-cont-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="cargo-cont-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Contenedor</label>
          <select required disabled={esEdicion} value={v.contenedores} onChange={(e) => seleccionaContenedor(e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...contenedores].sort((a, b) => (a.numeroInterno > b.numeroInterno ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.numeroInterno}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Cliente</label>
          <input type="text" disabled value={clienteNombre} className="input-base" />
        </div>
        <div>
          <label className="label">Concepto</label>
          <select required value={v.conceptosGastos} onChange={(e) => set('conceptosGastos', e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Descripcion</label>
          <input type="text" required value={v.descripcion} onChange={(e) => set('descripcion', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Moneda</label>
          <select value={v.moneda} onChange={(e) => cambiaMoneda(e.target.value)}
            className="select-base">
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>
        {v.moneda === 'USD' ? (
          <>
            <div>
              <label className="label">Total USD</label>
              <input type="number" min="0" step="any" required value={v.totalUSD} onChange={(e) => cambiaTotalUSD(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" min="0" step="any" required value={v.tipoCambio} onChange={(e) => cambiaTipoCambio(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Total MXN</label>
              <input type="number" disabled value={v.total.toFixed(2)} className="input-base" />
            </div>
          </>
        ) : (
          <div>
            <label className="label">Total MXN</label>
            <input type="number" min="0" step="any" required value={v.total} onChange={(e) => cambiaTotalMXN(e.target.value)}
              className="input-base" />
          </div>
        )}
      </form>
    </Modal>
  );
}

export default function CargosContenedorPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${RECURSO}:ver`);
  const puedeEditar = hasPermiso(`${RECURSO}:editar`);
  const puedeEliminar = hasPermiso(`${RECURSO}:eliminar`);

  const [cargos, setCargos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [contenedores, setContenedores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [cargosData, concData, contData] = await Promise.all([
        get(`/${ROUTE}`),
        apiClient.get('/conceptos-gastos', { params: { limit: 500 } }).then((r) => r.data.data),
        apiClient.get('/contenedores', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setConceptos(concData || []);
      setContenedores(contData || []);
      setCargos((cargosData || []).map((c) => {
        const conc = primero(c.conceptosGastos);
        const cont = primero(c.contenedores);
        const cli = primero(c.clientes);
        return {
          _id: c._id,
          numero: c.idCargoContenedor,
          fecha: c.fecha,
          descripcion: c.descripcion,
          concepto: conc?.name || '—',
          _idConcepto: conc?._id,
          contenedor: cont?.numeroInterno || '—',
          _idContenedor: cont?._id,
          cliente: cli?.nombre_comercial || '—',
          _idCliente: cli?._id,
          totalUSD: c.totalUSD,
          tipoCambio: c.tipoCambio,
          total: c.total,
          abonos: c.abonos,
          saldo: c.saldo,
          moneda: c.moneda,
          is_active: c.is_active,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Filtros de V1 (`ListadoCargosContenedor.jsx`): Fecha Inicio / Fecha Fin (default
  // últimos 30 días) y Cliente, aplicados antes de búsqueda/orden/exportación.
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(daysAgo(30));
  const [selectedFechaFin, setSelectedFechaFin] = useState(today());
  const [selectedCliente, setSelectedCliente] = useState('');
  const clientesFiltro = useMemo(() => {
    const m = new Map();
    cargos.forEach((c) => { if (c._idCliente) m.set(c._idCliente, c.cliente); });
    return [...m.entries()].sort((a, b) => (a[1] > b[1] ? 1 : -1));
  }, [cargos]);
  const cargosFiltrados = useMemo(
    () => cargos.filter((c) =>
      (!selectedFechaInicio || c.fecha >= selectedFechaInicio) &&
      (!selectedFechaFin || c.fecha <= selectedFechaFin) &&
      (!selectedCliente || c._idCliente === selectedCliente)
    ),
    [cargos, selectedFechaInicio, selectedFechaFin, selectedCliente]
  );

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(cargosFiltrados, {
    searchFields: ['concepto', 'contenedor', 'cliente', 'descripcion'], perPage: 50,
    defaultSortBy: 'fecha', defaultSortOrder: 'desc',
  });

  // Export PDF/Excel (mismo helper que GastosContenedorPage.jsx) — preserva el
  // `exportable:true` que tenía la config genérica de `resources.finanzas-cxc.jsx`
  // antes de que esta página se volviera bespoke por la calculadora ligada.
  // PDF/Excel de V1: Fecha, Concepto, Descripcion, Importe (+ Total).
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'concepto', label: 'Concepto' },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'total', label: 'Importe', render: (r) => formatMoney(r.total) },
  ];
  const totalFiltrado = filtradas.reduce((t, r) => t + (Number(r.total) || 0), 0);
  const opcionesPdf = { fileName: 'DetalleCargos', totalCols: { descripcion: 'Total', total: formatMoney(totalFiltrado) } };

  async function guardar(valores) {
    const payload = {
      fecha: valores.fecha,
      totalUSD: Number(valores.totalUSD),
      tipoCambio: Number(valores.tipoCambio),
      total: Number(valores.total),
      descripcion: valores.descripcion,
      conceptosGastos: valores.conceptosGastos,
      contenedores: valores.contenedores,
      clientes: valores.clientes,
      moneda: valores.moneda,
    };
    // Solo edición: V1 no crea cargos desde este listado (se crean desde Admin -> Contenedores).
    if (!editando) return;
    await patch(`/${ROUTE}/${editando._id}/importe`, payload);
    await cargar();
  }

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'concepto', label: 'Concepto', sortable: true },
    { key: 'descripcion', label: 'Descripcion', sortable: true },
    { key: 'contenedor', label: 'Contenedor', sortable: true },
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'total', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.total) },
    {
      key: '__acciones', label: 'Editar', sortable: false, className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Guard "no editar si ya tiene abonos" (segunda ronda de auditoría CxC
              2026-09-28, ver DUDAS.md) -- réplica exacta de
              `ListadoCargosContenedor.jsx` L695 (`c.saldo == c.total`): faltaba en
              este bespoke, cualquier Cargo de Contenedor se podía editar siempre. */}
          {puedeEditar && row.is_active !== 'No' && puedeEditarCargoContenedor(row) && (
            <button title="Editar" aria-label="Editar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => { setEditando(row); setFormOpen(true); }}>
              <Pencil size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Cargos Contenedores</h1>
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
          className="input-base w-64" />
        <button type="button" onClick={() => exportTablePdf('Detalle de Cargos', columnasExport, filtradas, opcionesPdf)}
          className="btn-outline text-xs px-3 py-1.5">PDF</button>
        <button type="button" onClick={() => setMailOpen(true)}
          className="btn-outline text-xs px-3 py-1.5">eMail</button>
        <button type="button" onClick={() => exportTableExcel('Detalle de Cargos', columnasExport, filtradas, { fileName: `DetalleCargos-${selectedFechaInicio}-${selectedFechaFin}`, sheetName: 'DetalleCargos' })}
          className="btn-outline text-xs px-3 py-1.5">Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={selectedFechaInicio} onChange={(e) => { setSelectedFechaInicio(e.target.value); setPage(1); }}
            className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={selectedFechaFin} onChange={(e) => { setSelectedFechaFin(e.target.value); setPage(1); }}
            className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => { setSelectedCliente(e.target.value); setPage(1); }}
            className="select-base">
            <option value="">Selecciona</option>
            {clientesFiltro.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
        </div>
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ cliente: 'TOTAL', total: formatMoney(pageRows.reduce((t, r) => t + (Number(r.total) || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Cargos"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Detalle de Cargos', columnasExport, filtradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Lista de Cargos', email, fileName: 'ListaCargos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Cargos.'),
          });
        }}
      />

      <CargoContenedorForm
        open={formOpen} onClose={() => setFormOpen(false)}
        title="Editar Cargo"
        initial={editando} conceptos={conceptos} contenedores={contenedores}
        onSubmit={guardar} esEdicion={!!editando}
      />
    </div>
  );
}
